import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
    Tag,
    Link,
    Flag,
    Image,
    Text,
    theme,
    Heading2,
    HelpToBuyLogo,
    ButtonWithIcon,
    ButtonLinkWithIcon
} from '@blueprint/ui';
import { getRems } from '../Layout/Layout.styles';
import { SubImageComponent } from './ListingCard.types';
import {
    ButtonPrev,
    ButtonNext
} from 'shared/components/Gallery/Gallery.styles';

const TransportWrapper = styled.div(css`
    padding: ${theme.spacing['spacing-2']} 0;
`);

const StyledWrapper = styled.div(css`
    border-bottom: solid 1px ${theme.colors['graphic-divider']};

    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        display: flex;
    }
`);

const ImageWrapper = styled.div(css`
    width: 100%;
    z-index: 2;
    position: relative;
    background: ${theme.colors['graphic-background-accent']};
    overflow: hidden;
    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        max-width: ${getRems(430)};
    }
`);

const SubImageContainer = styled(Image)<SubImageComponent>(
    ({ selected, theme: { colors } }) => css`
        width: ${getRems(69)};
        height: ${getRems(50)};
        display: inline-block;
        margin-top: ${theme.spacing['4px']};
        margin-right: ${theme.spacing['4px']};

        border-bottom: ${selected
            ? `solid ${colors['purple-bright']} ${theme.spacing['4px']}`
            : `solid ${colors['white']} ${theme.spacing['4px']}`};
        opacity: ${selected ? 0.65 : 1};

        ${theme.mediaQueries.medium} {
            width: ${getRems(105.25)};
            height: ${getRems(76)};
            cursor: pointer;
        }
    `
);

const SubImageWrapper = styled.div(
    ({ theme: { colors } }) => css`
        display: flex;
        justify-content: center;
        background: ${colors['white']};

        ${SubImageContainer}:last-child {
            margin-right: 0px;
        }
    `
);

const ImageContainer = styled.div(css`
    position: relative;
    width: 100%;
    min-height: ${getRems(207)};
    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        max-width: ${getRems(430)};
        min-height: ${getRems(310)};
    }
`);

const ImageCenterWrapper = styled.div(css`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    width: 100%;
    min-height: ${getRems(207)};
    max-height: ${getRems(207)};

    ${theme.mediaQueries.medium} {
        max-width: ${getRems(430)};
        min-height: ${getRems(310)};
        max-height: ${getRems(207)};
    }
`);

const DateDetailWrapper = styled.div(css`
    display: block;
    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        display: flex;
    }
`);

const SaveListingButton = styled.button(css`
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    font-family: inherit;

    &:focus {
        outline: 0;
        box-shadow: ${theme.focusRing};
    }
`);

const ViewCountContainer = styled.span(css`
    display: block;
    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        margin-left: ${theme.spacing['spacing-2']};
        display: flex;
    }
`);

const AvailableFromContainer = ViewCountContainer;

const StyledContent = styled.div(css`
    position: relative;
    flex-grow: 1;

    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        justify-content: center;
        align-items: center;
        margin: 0;
    }
`);

const StyledLink = styled(Link)(css`
    display: block;
    padding: ${theme.spacing['spacing-2']};
    width: 100%;
    height: 100%;

    @media only screen and (min-width: ${theme.breakpoints[1]}px) {
        padding: ${theme.spacing['spacing-5']}, ${theme.spacing['spacing-3']};
    }
`);

const JustAddedFlag = styled(Flag)(css`
    position: absolute;
    margin: ${theme.spacing['spacing-2']} 0 0 ${theme.spacing['spacing-2']};
    top: 0;
`);

const StyledHelpToBuy = styled(HelpToBuyLogo)(css`
    position: absolute;
    right: ${theme.spacing['spacing-2']};
    top: ${theme.spacing['spacing-2']};
`);

const WrapperFeatures = styled.div(css`
    display: flex;
    padding-top: ${theme.spacing['spacing-1']};
`);

const StyledAddress = styled(Heading2)(css`
    padding-top: ${theme.spacing['spacing-1']};
`);

const StyledTag = styled(Tag)(css`
    margin-right: ${theme.spacing['spacing-1']};
`);

const CardFooter = styled.div(css`
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    padding: ${theme.spacing['spacing-2']};
    align-items: center;
`);

const CardFooterCell = styled.div(css`
    display: flex;
    align-items: center;
    z-index: 2;
    ${ButtonWithIcon} {
        padding: 0;
        margin: 0;
        min-width: unset;
    }
`);

// TODO: alias box-shadow to in-page-low
const Wrapper = styled.div(
    ({ theme: { mq, borderRadius, boxShadow } }) => css`
        border: solid 0 ${theme.colors.white};
        position: relative;
        background-color: ${theme.colors.white};
        border: solid 1px ${theme.colors['graphic-divider']};
        border-radius: ${borderRadius['300']};
        overflow: hidden;

        :hover {
            box-shadow: ${boxShadow['100']};
        }

        ${ButtonPrev}, ${ButtonNext} {
            display: none;
        }

        ${mq.medium} {
            :hover {
                ${ButtonPrev}, ${ButtonNext} {
                    display: flex;
                }
            }
        }
    `
);

const CardHeader = styled.div`
    position: relative;
    display: flex;
    justify-content: space-between;
`;

const PriceContainer = styled.div(css`
    ${Flag} {
        margin-bottom: ${theme.spacing['8px']};
        margin-right: ${theme.spacing['4px']};
        float: left;
        clear: both;

        &:last-of-type {
            margin-right: 0;
        }

        ${theme.mq.small} {
            float: none;
        }
    }
`);

const PriceTitleText = styled(Text)(css`
    clear: both;
`);

const AgencyLogoLink = styled(Link)(css`
    position: absolute;
    right: 0;
    top: 0;
    z-index: 2;
    line-height: 1;
    max-width: ${getRems(73)};
    margin: ${theme.spacing['spacing-2']};

    ${Image} {
        max-height: ${getRems(42)};
        width: auto;
        display: block;
    }

    ${Text} {
        text-align: right;
    }
`);

const MediaLinksContainer = styled.div(css`
    position: absolute;
    bottom: ${theme.spacing['spacing-2']};
    right: ${theme.spacing['spacing-1']};
    ${Tag} {
        display: inline-flex;
        align-items: center;
        margin-right: ${theme.spacing['spacing-1']};
        background-color: ${theme.colors['purple-dark-opacity80']};
        color: ${theme.colors.white};
        svg {
            margin-right: ${theme.spacing['micro-spacing-2']};
        }
    }
`);

const ImageLink = styled(Link)(css`
    display: contents;
`);

const ContactLink = styled(ButtonLinkWithIcon)(
    (props) => css`
        display: none;
        margin: 0 ${props.theme.spacing['24px']} 0 0;
        padding: 0;
        min-width: unset;

        &:focus {
            outline: 0;
            box-shadow: ${props.theme.focusRing};
        }

        ${theme.mq.medium} {
            display: inline-block;
        }
    `
);

const PriceDropText = styled(Text)(
    ({ theme }) => css`
        margin: ${theme.spacing['16px']} 0;
        clear: both;

        strong {
            font-weight: ${theme.type.semiBold};
            color: ${theme.colors['crimson']};
        }
    `
);

const PageGalleryContent = styled('div')(css`
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
`);

export {
    TransportWrapper,
    StyledWrapper,
    ImageWrapper,
    ImageContainer,
    ImageCenterWrapper,
    DateDetailWrapper,
    ViewCountContainer,
    JustAddedFlag,
    StyledHelpToBuy,
    WrapperFeatures,
    StyledAddress,
    StyledTag,
    CardFooter,
    CardFooterCell,
    Wrapper,
    CardHeader,
    PriceContainer,
    AgencyLogoLink,
    MediaLinksContainer,
    SaveListingButton,
    SubImageContainer,
    SubImageWrapper,
    ContactLink,
    ImageLink,
    AvailableFromContainer,
    PriceDropText,
    PriceTitleText,
    PageGalleryContent,
    StyledContent,
    StyledLink
};
